import { CONFIG } from '@/constants/Config';
import type { CacheEntry } from '@/types';

const CACHE_PREFIX = 'tifosi_cache_';

class StorageService {
  async get<T>(key: string): Promise<T | null> {
    try {
      const data = localStorage.getItem(CACHE_PREFIX + key);
      if (!data) return null;

      const entry: CacheEntry<T> = JSON.parse(data);

      if (this.isExpired(entry.timestamp, entry.ttl)) {
        return null;
      }

      return entry.data;
    } catch (error) {
      console.error('Storage get error:', error);
      return null;
    }
  }

  async set<T>(key: string, data: T, ttl: number = CONFIG.CACHE_TTL): Promise<void> {
    try {
      const entry: CacheEntry<T> = {
        key,
        data,
        timestamp: Date.now(),
        ttl,
      };
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
    } catch (error) {
      console.error('Storage set error:', error);
    }
  }

  async getWithMeta<T>(key: string): Promise<CacheEntry<T> | null> {
    try {
      const data = localStorage.getItem(CACHE_PREFIX + key);
      if (!data) return null;
      return JSON.parse(data);
    } catch (error) {
      console.error('Storage getWithMeta error:', error);
      return null;
    }
  }

  async delete(key: string): Promise<void> {
    try {
      localStorage.removeItem(CACHE_PREFIX + key);
    } catch (error) {
      console.error('Storage delete error:', error);
    }
  }

  async clear(): Promise<void> {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith(CACHE_PREFIX)) keysToRemove.push(k);
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (error) {
      console.error('Storage clear error:', error);
    }
  }

  private isExpired(timestamp: number, ttl: number = CONFIG.CACHE_TTL): boolean {
    return Date.now() - timestamp > ttl;
  }
}

export const storageService = new StorageService();
export default storageService;
